import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import {ApModalReason} from './ApModalReason';

export type ApModalResult<T> =
  ApModalResultSuccess<T> |
  ApModalResultFailure;

export interface ApModalResultSuccess<TResult> {
  readonly isSuccess: true;
  readonly reason: ApModalReason.Closed;
  readonly resultValue: TResult;
}

export interface ApModalResultFailure {
  readonly isSuccess: false;
  readonly reason: ApModalReason.UserCancel | ApModalReason.Unknown;

  // this could be an Error, error message, general reason for cancellation, etc
  readonly failureData?: any;
}

// This a guard to help for narrowing to ApModalResultSuccess or ApModalResultFailure
export function isModalSuccess<T>(modalResult: ApModalResult<T>): modalResult is ApModalResultSuccess<T> {
  return modalResult.isSuccess;
}

export function mapToModalSuccessValue<T>() {
  return (source: Observable<ApModalResult<T>>): Observable<T> => {
    return source.pipe(
      filter(isModalSuccess),
      map(r => r.resultValue),
    );
  };
}

// this is an operator that just uses map with convertModalResult
// Use it when you want to map a successful result to a different type,
// and just let a failure result get returned as normal.
export function convertModalResult$<TSource, TTarget>(mapFn: (value: TSource) => TTarget) {
  return map<ApModalResult<TSource>, ApModalResult<TTarget>>((modalResult) => convertModalResult(modalResult, mapFn));
}

export function convertModalResult<TSource, TTarget>(
  source: ApModalResult<TSource>,
  mapFn: (value: TSource) => TTarget,
): ApModalResult<TTarget> {
  if (!source.isSuccess) {
    return source;
  }

  return {
    isSuccess: source.isSuccess,
    reason: source.reason,
    resultValue: mapFn(source.resultValue),
  } satisfies ApModalResultSuccess<TTarget>;
}

export function apModalClosed<T>(result: T): ApModalResult<T> {
  return {
    isSuccess: true,
    reason: ApModalReason.Closed,
    resultValue: result,
  };
}

export function apModalCancelled<T>(
  reason: ApModalReason.UserCancel | ApModalReason.Unknown,
  failureData?: any,
): ApModalResult<T> {
  return {
    isSuccess: false,
    reason,
    failureData,
  };
}
